import { createSlice } from '@reduxjs/toolkit';
import { callApi } from '../../utils/apiCall';
import { hasFeatureAccess } from '../../utils/featurePermission';
import { dispatch } from '../store';
import { updateError } from './user';
import { checkPmsEnabled } from './pmsSchedule';
import { seedDateFormatsFromPractice } from '../../utils/dataGridUtils';
import { clearCached } from '../../utils/staticLookupCache';
import { getPracticeContextCacheKey } from './mergeTags';

// Read cached Dentally enabled state for instant UI on load
const cachedDentallyEnabled = (() => {
    try {
        return localStorage.getItem('dentallyEnabledGlobal') === 'true';
    } catch {
        return false;
    }
})();

const initialState = {
    isLoading: false,
    practice: {},
    clickUpUrlGlobal: '',
    dentallyEnabledGlobal: cachedDentallyEnabled,
};

const slice = createSlice({
    name: 'practice',
    initialState,
    reducers: {
        reset(state) {
            Object.assign(state, { ...initialState, dentallyEnabledGlobal: false });
            try { localStorage.removeItem('dentallyEnabledGlobal'); } catch { /* noop */ }
        },
        getPracticeData(state, action) {
            state.isLoading = false;
            state.practice = action.payload;
        },
        // HAS ERROR
        hasError(state, action) {
            state.isLoading = false;
            state.error = action.payload;
        },
        updateLoader(state, action) {
            state.isLoading = action.payload;
        },
        updateClickUpUrlGlobal(state, action) {
            state.clickUpUrlGlobal = action.payload;
        },
        updateDentallyEnabledGlobal(state, action) {
            state.dentallyEnabledGlobal = action.payload;
            try { localStorage.setItem('dentallyEnabledGlobal', String(action.payload)); } catch { /* noop */ }
        }
    }
});

// Reducer
export default slice.reducer;

export const { getPracticeData, reset, updateClickUpUrlGlobal, updateDentallyEnabledGlobal } = slice.actions;

// Standalone fast check — reads from PracticeDentally config (is_active flag).
// Checks the feature flag FIRST to avoid 403 errors for practices without Dentally.
export function checkDentallyEnabled(practiceId) {
    return async () => {
        try {
            // Pass practiceId so the gate refuses stale-practice fires during a
            // practice-switch window (otherwise the bootstrap-safe path lets the
            // call through and the BE returns 403 — DENTALSEM-CRM-1BS3 regression).
            // getPracticeItem dispatches this on every practice load, so it's the
            // single hottest path for the dentally 403 flood.
            if (!hasFeatureAccess('dentally-integration', practiceId)) {
                dispatch(slice.actions.updateDentallyEnabledGlobal(false));
                return;
            }

            const response = await callApi(`/api/v1/practices/${practiceId}/dentally/`);
            const results = response?.data?.results || [];
            const isEnabled = results.some((config) => config.is_active);

            dispatch(slice.actions.updateDentallyEnabledGlobal(isEnabled));
        } catch {
            dispatch(slice.actions.updateDentallyEnabledGlobal(false));
        }
    };
}

export function getPracticeItem(id) {
    return async () => {
        try {
            dispatch(slice.actions.updateLoader(true));
            // Fetch practice data and PMS check in parallel.
            // NOTE: checkDentallyEnabled is deliberately NOT dispatched here —
            // during post-login bootstrap the permissions snapshot is empty and
            // hasFeatureAccess() is bootstrap-safe (allows), so probing here
            // 403s for every non-dentally practice on every hard load. The
            // dashboard header effect (header/index.jsx) is the sole dispatcher:
            // it waits for dentallyFeature.canView from the settled snapshot.
            const [response] = await Promise.all([
                callApi(`/api/v1/practices/${id}/`),
                dispatch(checkPmsEnabled(id, { force: true })),
            ]);

            // ISS-013 — never let a failed fetch poison state.practice.
            // callApi does not throw: on any transport failure (abort, timeout,
            // offline, blocked path guard) it returns { ok:false, status:0,
            // data:null } (apiCall.js:99-106). Storing that null replaced the
            // whole practice object, and every consumer doing
            // Object.keys(practice) then threw "Cannot convert undefined or
            // null to object" and unmounted the tree at the ErrorBoundary —
            // e.g. ConsentBuilderPopup.jsx:20 white-screening the V1 consent
            // forms popup. Keeping the last-good value is strictly better than
            // a blank dashboard: the loader stays up and a retry can recover.
            if (!response?.ok || !response?.data) {
                dispatch(slice.actions.updateLoader(false));
                console.warn('[practice] getPracticeItem: no practice body, keeping last-good state', {
                    practiceId: id,
                    status: response?.status,
                });
                return;
            }

            dispatch(slice.actions.getPracticeData(response.data));
            seedDateFormatsFromPractice(response.data?.preferences?.date_format_config);

            if(response.data?.metadata && response.data.metadata.length > 0){
                const isClickUpUrl = response.data.metadata.find(item => item.key === 'clickup_url');
                if(isClickUpUrl){
                    dispatch(slice.actions.updateClickUpUrlGlobal(isClickUpUrl.value));
                }
            }
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}


export function updatePractice({ id, data, isEmergencyAppointment = false }) {
    return async () => {
        try {
            dispatch(slice.actions.updateLoader(true));
            const response = await callApi(`/api/v1/practices/${id}/`, 'patch', data);
            if (response.status === 200) {
                dispatch(slice.actions.getPracticeData(response.data));
                if (Object.prototype.hasOwnProperty.call(data?.preferences || {}, 'is_pms_scheduling_enabled')) {
                    dispatch(checkPmsEnabled(id, { force: true }));
                }
                seedDateFormatsFromPractice(response.data?.preferences?.date_format_config);
                // Invalidate the cached get_context:practice:<id> merge-tag payload
                // (src/redux/slices/mergeTags.js) — this PATCH can change fields
                // surfaced through get_context, and without this a reload within
                // the 10-min TTL would serve pre-edit data.
                clearCached(getPracticeContextCacheKey(id));

                if(response.data.metadata && response.data.metadata.length > 0){
                    const isClickUpUrl = response.data.metadata.find(item => item.key === 'clickup_url');
                    if(isClickUpUrl){
                        dispatch(slice.actions.updateClickUpUrlGlobal(isClickUpUrl.value));
                    }
                }

                if (isEmergencyAppointment) {
                    dispatch(updateError({ success: true, message: 'Data updated successfully.' }));
                    return;
                }
                dispatch(updateError({ success: true, message: 'Data updated successfully. Please refresh the page' }));
            } else {
                // ISS-018 — the optional chain used to stop one link short of
                // .toString(), so this error handler threw its own TypeError
                // whenever the body had no settings.consent_base_url. It also
                // showed the user a consent base URL as an error message, which
                // means nothing to them. Prefer the API's own detail/message.
                // ISS-030 — the loader was only cleared as a side effect of
                // getPracticeData on the 200 path, so a failed save left
                // practice.isLoading true forever and the settings form stayed
                // spinning. Clear it explicitly on both failure paths.
                dispatch(slice.actions.updateLoader(false));
                dispatch(updateError({
                    success: false,
                    message: response?.data?.detail
                        || response?.data?.message
                        || 'Could not save practice settings. Please try again.',
                }));
            }
        } catch (error) {
            dispatch(slice.actions.updateLoader(false));
            dispatch(updateError({ message: JSON.stringify(error.message) || 'Something went wrong', success: false }));
        }
    };
}

export function generateConsentUrl(id, data, handleCopy) {
    return async () => {
        try {
            dispatch(slice.actions.updateLoader(true));
            const response = await callApi(`/api/v1/practices/${id}/generate_consent_url/`, 'post', data);
            if (response.status === 200) {
                handleCopy(response.data);
                dispatch(updateError({ success: true, message: 'Consent link generated successfully' }));
            } else {
                dispatch(updateError({ success: false, message: response?.data.practice_user?.toString() || 'Something went wrong' }));
            }
        } catch (error) {
            dispatch(updateError({ message: JSON.stringify(error.message) || 'Something went wrong', success: false }));
        }
    };
}